import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useMemo } from 'react';
import { Download, Calendar, Loader2, AlertCircle, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const PCBBroadSheetView = ({ initialYear = '2024' }) => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [broadsheetData, setBroadsheetData] = useState([]);

  // Year & Employee Filters
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedDepartment, setSelectedDepartment] = useState('ALL');
  
  const fmt = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const num = Number(val);
    if (isNaN(num)) return val;
    return '₹' + Math.round(num).toLocaleString('en-IN');
  };

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('ALL');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [isEditingYear, setIsEditingYear] = useState(false);
  const [tempYearInput, setTempYearInput] = useState(selectedYear);

  useEffect(() => {
    setTempYearInput(selectedYear);
  }, [selectedYear]);

  const handleYearSubmit = () => {
    setIsEditingYear(false);
    const num = parseInt(tempYearInput, 10);
    if (!isNaN(num) && num >= 1990 && num <= 2100) {
      setSelectedYear(String(num));
    } else {
      setTempYearInput(selectedYear);
    }
  };

  const [allDepartmentsList, setAllDepartmentsList] = useState([]);
  const [allEmployeesList, setAllEmployeesList] = useState([]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/v1/departments'), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            const names = data.map((d) => d.name).filter(Boolean);
            setAllDepartmentsList(names);
          }
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  const loadBroadSheetData = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `/api/v1/reports/pf-broadsheet?year=${selectedYear}`;
      if (selectedDepartment && selectedDepartment !== 'ALL') {
        url += `&department=${encodeURIComponent(selectedDepartment)}`;
      }
      if (selectedEmployeeId && selectedEmployeeId !== 'ALL') {
        url += `&employee_id=${selectedEmployeeId}`;
      }

      const response = await fetch(buildApiUrl(url), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load Broad Sheet report data');
      }
      const data = await response.json();
      const broadsheets = data.broadsheets || [];
      setBroadsheetData(broadsheets);

      if (data.all_departments && Array.isArray(data.all_departments) && data.all_departments.length > 0) {
        setAllDepartmentsList((prev) => Array.from(new Set([...prev, ...data.all_departments])));
      }

      // If selectedEmployeeId is ALL or if allEmployeesList is empty, populate allEmployeesList
      if (selectedEmployeeId === 'ALL' || allEmployeesList.length === 0) {
        const fetchedEmps = broadsheets.map((emp) => ({
          employee_id: emp.employee_id,
          emp_code: emp.emp_code,
          emp_name: emp.emp_name,
          department: emp.department,
        }));
        setAllEmployeesList((prev) => {
          const map = new Map();
          prev.forEach((item) => map.set(item.employee_id, item));
          fetchedEmps.forEach((item) => map.set(item.employee_id, item));
          return Array.from(map.values());
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error loading Broad Sheet data');
    } finally {
      setLoading(false);
    }
  };

  // Reset employee list when year or department changes to ensure fresh data
  useEffect(() => {
    loadBroadSheetData();
  }, [selectedYear, selectedDepartment, selectedEmployeeId]);

  // Extract all departments for dropdown
  const availableDepartments = useMemo(() => {
    const setOfDepts = new Set(allDepartmentsList);
    broadsheetData.forEach((b) => {
      if (b.department) setOfDepts.add(b.department);
    });
    return Array.from(setOfDepts).sort();
  }, [allDepartmentsList, broadsheetData]);

  // Extract available employees for dropdown without losing list when 1 employee is selected
  const availableEmployees = useMemo(() => {
    let list = allEmployeesList;
    if (selectedDepartment && selectedDepartment !== 'ALL') {
      list = list.filter((e) => e.department === selectedDepartment);
    }
    return [...list].sort((a, b) => (a.emp_code || '').localeCompare(b.emp_code || ''));
  }, [allEmployeesList, selectedDepartment]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      let url = `/api/v1/reports/pf-broadsheet-pdf?year=${selectedYear}`;
      if (selectedDepartment && selectedDepartment !== 'ALL') {
        url += `&department=${encodeURIComponent(selectedDepartment)}`;
      }
      if (selectedEmployeeId && selectedEmployeeId !== 'ALL') {
        url += `&employee_id=${selectedEmployeeId}`;
      }

      await downloadAuthenticatedFile(
        url,
        `PF_Broadsheet_${selectedYear}_${parseInt(selectedYear) + 1}.pdf`
      );
    } catch (err) {
      console.error('Error downloading PF Broad Sheet PDF:', err);
      window.print();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Filter & Control Bar */}
      <div className="print:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 w-full">
          <div className="flex flex-wrap items-center gap-4">
            <Calendar className="w-5 h-5 text-amber-600 self-center" />

            {/* Financial Year Selector (2-Year Badge with Steppers & Inline Edit) */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">FINANCIAL YEAR</label>
              <div className="flex items-center space-x-1">
                {/* Previous Year Button */}
                <button
                  type="button"
                  onClick={() => setSelectedYear((prev) => String(Number(prev) - 1))}
                  className="p-1.5 border border-gray-300 rounded-lg text-gray-600 bg-white hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all shadow-sm"
                  title="Previous Financial Year"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Clickable 2-Year Financial Year Badge / Editable Field */}
                {isEditingYear ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    autoFocus
                    value={tempYearInput}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setTempYearInput(val);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleYearSubmit();
                      if (e.key === 'Escape') setIsEditingYear(false);
                    }}
                    onBlur={handleYearSubmit}
                    className="w-20 px-2 py-1 border-2 border-amber-500 rounded-lg text-sm font-bold text-center bg-amber-50 text-amber-900 focus:outline-none shadow-inner"
                    placeholder="2027"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsEditingYear(true)}
                    className="px-3.5 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-gray-800 bg-gradient-to-b from-white to-gray-50 hover:from-amber-50 hover:to-amber-100 hover:border-amber-400 hover:text-amber-900 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all shadow-sm flex items-center justify-center min-w-[110px]"
                    title="Click to type custom start year"
                  >
                    <span>{selectedYear}-{String(Number(selectedYear) + 1).slice(-2)}</span>
                  </button>
                )}

                {/* Next Year Button */}
                <button
                  type="button"
                  onClick={() => setSelectedYear((prev) => String(Number(prev) + 1))}
                  className="p-1.5 border border-gray-300 rounded-lg text-gray-600 bg-white hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all shadow-sm"
                  title="Next Financial Year"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Department Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">DEPARTMENT</label>
              <select
                value={selectedDepartment}
                onChange={(e) => {
                  setSelectedDepartment(e.target.value);
                  setSelectedEmployeeId('ALL');
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:outline-none min-w-[180px]"
              >
                <option value="ALL">All Departments</option>
                {availableDepartments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* Employee Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">EMPLOYEE</label>
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:outline-none min-w-[220px]"
              >
                <option value="ALL">All Employees</option>
                {availableEmployees.map((emp) => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.emp_code} - {emp.emp_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {hasPermission('report:export') && (
<button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf || loading || broadsheetData.length === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 shadow-sm"
            >
              {downloadingPdf ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
)}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Broad Sheet Document Display */}
      <div className="bg-white p-6 sm:p-10 rounded-xl border border-gray-300 shadow-lg text-gray-900 font-serif">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-amber-600 animate-spin mb-3" />
            <p className="text-gray-500 font-medium font-sans">Loading Broad Sheet data...</p>
          </div>
        ) : broadsheetData.length === 0 ? (
          <div className="text-center py-16 text-gray-500 font-sans">
            <p className="text-lg font-medium mb-1">No Broad Sheet data found</p>
            <p className="text-sm text-gray-400">Please select another year or employee.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {broadsheetData.map((emp, empIdx) => (
              <div key={emp.employee_id} className={`space-y-6 ${empIdx > 0 ? 'pt-12 border-t-2 border-dashed border-gray-300 page-break-before' : ''}`}>
                {/* Header Titles */}
                <div className="text-center space-y-1">
                  <h1 className="text-xl font-bold tracking-tight text-black">
                    पुणे छावणी परिषद
                  </h1>
                  <h2 className="text-sm font-bold text-black uppercase tracking-wide">
                    छावणी कर्मचारियों का भविष्य निधि लेखा : वर्ष {emp.fin_year}
                  </h2>
                  <h3 className="text-sm font-bold text-black uppercase tracking-wide">
                    CANTONMENT SERVANTS PROVIDENT FUND ACCOUNT FOR THE YEAR {emp.fin_year}
                  </h3>
                </div>

                {/* Metadata Line */}
                <div className="flex flex-wrap items-center justify-between text-sm font-bold border-b border-black pb-2 pt-2">
                  <div>
                    कोड नं : <span className="underline ml-1">{emp.pf_number}</span>
                  </div>
                  <div>
                    / {emp.department} नाम : <span className="underline ml-2 uppercase">{emp.emp_name}</span>
                  </div>
                </div>

                {/* Main Broad Sheet Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs font-mono border-collapse border-2 border-black bg-white text-black">
                    <thead>
                      <tr className="border-b border-black text-center font-bold uppercase bg-gray-50">
                        <th rowSpan="2" className="border-r border-black p-2 min-w-[130px]">MONTH</th>
                        <th colSpan="3" className="border-r border-black p-2">DEPOSITS</th>
                        <th colSpan="2" className="border-r border-black p-2">DEBITS</th>
                        <th rowSpan="2" className="p-2 min-w-[130px]">BROADSHEET<br/>TOTAL</th>
                      </tr>
                      <tr className="border-b border-black text-center font-bold uppercase bg-gray-50">
                        <th className="border-r border-black p-1.5 w-[110px]">
                          BY DEDUCTION FROM SALARY
                        </th>
                        <th className="border-r border-black p-1.5 w-[110px]">
                          VOLUNTARY CONTRIBUTION
                        </th>
                        <th className="border-r border-black p-1.5 w-[110px]">
                          ADVANCE REPAID
                        </th>
                        <th className="border-r border-black p-1.5 w-[110px]">
                          REFUNDABLE AMOUNT PAID
                        </th>
                        <th className="border-r border-black p-1.5 w-[120px]">
                          NON REFUNDABLE AMOUNT PAID
                        </th>
                      </tr>
                      <tr className="border-b border-black text-center font-bold text-xs bg-gray-100">
                        <td className="border-r border-black p-1"></td>
                        <td className="border-r border-black p-1">(1)</td>
                        <td className="border-r border-black p-1">(2)</td>
                        <td className="border-r border-black p-1">(3)</td>
                        <td className="border-r border-black p-1">(4)</td>
                        <td className="border-r border-black p-1">(5)</td>
                        <td className="p-1"></td>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Initial Opening Balance Row above April */}
                      <tr className="border-b border-black text-center font-bold">
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="p-1.5 text-center">{fmt(emp.opening_balance)}</td>
                      </tr>

                      {/* 12 Monthly Rows */}
                      {emp.monthly_rows.map((m) => (
                        <tr key={m.month_code} className="border-b border-gray-400 hover:bg-amber-50/40">
                          <td className="border-r border-black p-1.5 text-center font-medium">
                            {m.month_label_hi}
                          </td>
                          <td className="border-r border-black p-1.5 text-center font-mono">
                            {fmt(m.pfs)}
                          </td>
                          <td className="border-r border-black p-1.5 text-center font-mono">
                            {fmt(m.vpf)}
                          </td>
                          <td className="border-r border-black p-1.5 text-center font-mono">
                            {fmt(m.advance_repaid)}
                          </td>
                          <td className="border-r border-black p-1.5 text-center font-mono">
                            {fmt(m.refundable_paid)}
                          </td>
                          <td className="border-r border-black p-1.5 text-center font-mono">
                            {fmt(m.non_refundable_paid)}
                          </td>
                          <td className="p-1.5 text-center font-bold font-mono">
                            {fmt(m.broadsheet_total)}
                          </td>
                        </tr>
                      ))}

                      {/* Total Row ("कुल") */}
                      <tr className="border-t-2 border-b border-black font-bold text-sm bg-gray-50">
                        <td className="border-r border-black p-2 text-center font-extrabold">
                          कुल
                        </td>
                        <td className="border-r border-black p-2 text-center font-mono font-bold">
                          {fmt(emp.tot_pfs)}
                        </td>
                        <td className="border-r border-black p-2 text-center font-mono font-bold">
                          {fmt(emp.tot_vpf)}
                        </td>
                        <td className="border-r border-black p-2 text-center font-mono font-bold">
                          {fmt(emp.tot_adv_repaid)}
                        </td>
                        <td className="border-r border-black p-2 text-center font-mono font-bold">
                          {fmt(emp.tot_refundable)}
                        </td>
                        <td className="border-r border-black p-2 text-center font-mono font-bold">
                          {fmt(emp.tot_non_refundable)}
                        </td>
                        <td className="p-2 text-center font-mono font-bold underline">
                          {fmt(emp.sum_broadsheet_totals)}
                        </td>
                      </tr>

                      {/* Interest Amount Sub-row */}
                      <tr className="border-b-2 border-black font-bold text-sm">
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="border-r border-black p-1.5"></td>
                        <td className="p-1.5 text-center font-mono font-bold">
                          {fmt(emp.interest_credited)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Bottom Summary Block */}
                <div className="pt-4 text-xs font-mono font-bold space-y-2 max-w-xl">
                  <div className="flex justify-between items-center">
                    <span>OPENING BALANCE AS ON 01.04.{emp.start_year}</span>
                    <span className="w-32 text-right">{fmt(emp.opening_balance)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>CREDITS DURING THE YEAR {emp.fin_year}</span>
                    <span className="w-32 text-right">{fmt(emp.tot_credits)}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-black pb-1">
                    <span>DEBITS DURING THE YEAR {emp.fin_year}</span>
                    <span className="w-32 text-right">{fmt(emp.tot_debits)}</span>
                  </div>
                  <div className="flex justify-between items-center font-extrabold text-sm pt-1">
                    <span>कुल / TOTAL</span>
                    <span className="w-32 text-right">{fmt(emp.tot_before_interest)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>INTEREST CREDITED DURING THE YEAR {emp.fin_year}</span>
                    <span className="w-32 text-right">{fmt(emp.interest_credited)}</span>
                  </div>
                  <div className="flex justify-between items-center font-extrabold text-sm border-t border-black pt-1">
                    <span>CLOSING BALANCE AS ON 31-3-{emp.end_year}</span>
                    <span className="w-32 text-right underline underline-offset-4 border-b-2 border-black pb-0.5">
                      {fmt(emp.closing_balance)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PCBBroadSheetView;
